import React, { useEffect, useMemo } from 'react'
import { Navigate, type NavigateProps, Route, Routes, useLocation, useParams } from 'react-router-dom'

import NotFoundPage from '../components/pages/notFoundPage/NotFoundPage'
import ErrorPage from '../components/pages/errorPage/ErrorPage'
import LoginPage from '../components/pages/loginPage/LoginPage'
import {
  AGENT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
  BASE_PATH,
  ERROR_PATH,
  FLOW_CHOOSER_PATH,
  FORBIDDEN_PATH,
  LISTING_DELETED_PATH,
  LOGIN_PATH,
  OLD_AGENT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
  OLD_PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
  OLD_TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
  PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
  SEGMENTATION_PATH,
  SESSION_NOT_FOUND_PATH,
  TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH,
} from '../services/routes/routes'
import { propertyOwnerOfficeRentFlow } from './flows/rent/propertyOwner/office/propertyOwnerOfficeRentFlow'
import FlowChooserPage from '../components/pages/flowChooserPage/FlowChooserPage'
import { propertyOwnerHangarRentFlow } from './flows/rent/propertyOwner/hangar/propertyOwnerHangarRentFlow'
import { propertyOwnerGarageRentFlow } from './flows/rent/propertyOwner/garage/propertyOwnerGarageRentFlow'
import { propertyOwnerGastronomyRentFlow } from './flows/rent/propertyOwner/gastronomy/propertyOwnerGastronomyRentFlow'
import { propertyOwnerHouseRentFlow } from './flows/rent/propertyOwner/house/desktop/propertyOwnerHouseRentFlow'
import { propertyOwnerStoreRentFlow } from './flows/rent/propertyOwner/store/propertyOwnerStoreRentFlow'
import { propertyOwnerApartmentSellFlow } from './flows/sell/propertyOwner/apartment/desktop/propertyOwnerApartmentSellFlow'
import { propertyOwnerGastronomySellFlow } from './flows/sell/propertyOwner/gastronomy/propertyOwnerGastronomySellFlow'
import { propertyOwnerHangarSellFlow } from './flows/sell/propertyOwner/hangar/propertyOwnerHangarSellFlow'
import { propertyOwnerHouseSellFlow } from './flows/sell/propertyOwner/house/desktop/propertyOwnerHouseSellFlow'
import { propertyOwnerIndustrialPlotSellFlow } from './flows/sell/propertyOwner/industrialPlot/propertyOwnerIndustrialPlotSellFlow'
import { propertyOwnerOfficeSellFlow } from './flows/sell/propertyOwner/office/propertyOwnerOfficeSellFlow'
import { propertyOwnerPlotSellFlow } from './flows/sell/propertyOwner/plot/propertyOwnerPlotSellFlow'
import { propertyOwnerStoreSellFlow } from './flows/sell/propertyOwner/store/propertyOwnerStoreSellFlow'
import { tenantApartmentRentFlow } from './flows/rent/tenant/apartment/desktop/tenantApartmentRentFlow'
import { webViewTenantApartmentRentFlow } from './flows/rent/tenant/apartment/webView/webViewTenantApartmentRentFlow'
import { tenantGarageRentFlow } from './flows/rent/tenant/garage/tenantGarageRentFlow'
import { tenantGastronomyRentFlow } from './flows/rent/tenant/gastronomy/tenantGastronomyRentFlow'
import { tenantHangarRentFlow } from './flows/rent/tenant/hangar/tenantHangarRentFlow'
import { tenantHouseRentFlow } from './flows/rent/tenant/house/desktop/tenantHouseRentFlow'
import { tenantOfficeRentFlow } from './flows/rent/tenant/office/tenantOfficeRentFlow'
import { tenantShortTermAccommodationRentFlow } from './flows/rent/tenant/shortTermAccommodation/tenantShortTermAccommodationRentFlow'
import { tenantStoreRentFlow } from './flows/rent/tenant/store/tenantStoreRentFlow'
import { useEnvironment } from '../utils/environment/useEnvironment'
import { InitState } from '../redux/reducers/root/initialState'
import { tenantFlatShareRoomRentFlow } from './flows/rent/tenant/flatShareRoom/tenantFlatShareRoomRentFlow'
import InsertionEditorWrapper from '../components/pages/insertionPage/wrapper/InsertionEditorWrapper'
import { useAppDispatch, useAppSelector, usePreserveSearchParamsNavigate } from './hooks'
import { agentApartmentRentFlow } from './flows/rent/agent/apartment/agentApartmentRentFlow'
import { propertyOwnerShortTermAccommodationRentFlow } from './flows/rent/propertyOwner/shortTermAccommodation/desktop/propertyOwnerShortTermAccommodationRentFlow'
import { propertyOwnerIndustrialPlotRentFlow } from './flows/rent/propertyOwner/industrialPlot/propertyOwnerIndustrialPlotRentFlow'
import { propertyOwnerInvestmentSellFlow } from './flows/sell/propertyOwner/investment/propertyOwnerInvestmentSellFlow'
import { agentFlatShareRoomRentFlow } from './flows/rent/agent/flatShareRoom/agentFlatShareRoomRentFlow'
import { isWebView, preserveSearchParams } from '../utils/navigation'
import SegmentationPage from '../components/pages/segmentationPage/SegmentationPage'
import ProtectedRoute from '../services/authentication/ProtectedRoute'
import { resetError } from '../redux/reducers/errorHandler/errorHandlerFeature'
import { tenantIndustrialPlotRentFlow } from './flows/rent/tenant/industrialPlot/tenantIndustrialPlotRentFlow'
import { agentShortTermAccommodationRentFlow } from './flows/rent/agent/shortTermAccommodation/agentShortTermAccommodationRentFlow'
import { agentGarageRentFlow } from './flows/rent/agent/garage/agentGarageRentFlow'
import { agentApartmentSellFlow } from './flows/sell/agent/apartment/agentApartmentSellFlow'
import { agentHouseSellFlow } from './flows/sell/agent/house/agentHouseSellFlow'
import { agentHouseRentFlow } from './flows/rent/agent/house/agentHouseRentFlow'
import { agentOfficeRentFlow } from './flows/rent/agent/office/agentOfficeRentFlow'
import { agentGastronomyRentFlow } from './flows/rent/agent/gastronomy/agentGastronomyRentFlow'
import { agentGastronomySellFlow } from './flows/sell/agent/gastronomy/agentGastronomySellFlow'
import { agentStoreRentFlow } from './flows/rent/agent/store/agentStoreRentFlow'
import { agentOfficeSellFlow } from './flows/sell/agent/office/agentOfficeSellFlow'
import { agentProductionHallRentFlow } from './flows/rent/agent/productionHall/agentProductionHallRentFlow'
import { agentIndustrialPlotSellFlow } from './flows/sell/agent/industrialPlot/agentIndustrialPlotSellFlow'
import { agentProductionHallSellFlow } from './flows/sell/agent/productionHall/agentProductionHallSellFlow'
import { agentStoreSellFlow } from './flows/sell/agent/store/agentStoreSellFlow'
import { agentPlotSellFlow } from './flows/sell/agent/plot/agentPlotSellFlow'
import { agentInvestmentSellFlow } from './flows/sell/agent/investment/agentInvestmentSellFlow'
import { agentIndustrialPlotRentFlow } from './flows/rent/agent/industrialPlot/agentIndustrialPlotRentFlow'
import { ListingParams } from '../components/pages/insertionPage/InsertionEditor'
import { agentGarageSellFlow } from './flows/sell/agent/garage/agentGarageSellFlow'
import { propertyOwnerGarageSellFlow } from './flows/sell/propertyOwner/garage/propertyOwnerGarageSellFlow'
import { webViewPropertyOwnerApartmentRentFlow } from './flows/rent/propertyOwner/apartment/webView/webViewPropertyOwnerApartmentRentFlow'
import { propertyOwnerApartmentRentFlow } from './flows/rent/propertyOwner/apartment/desktop/propertyOwnerApartmentRentFlow'
import { agentWaitingListApartmentRentFlow } from './flows/rent/agent/waitingListApartment/agentWaitingListApartmentRentFlow'
import { FlowName } from '../@types/flow-types'
import { webViewPropertyOwnerHouseRentFlow } from './flows/rent/propertyOwner/house/webView/webViewPropertyOwnerHouseRentFlow'
import { webViewPropertyOwnerHouseSellFlow } from './flows/sell/propertyOwner/house/webView/webViewPropertyOwnerHouseSellFlow'
import { webViewPropertyOwnerShortTermAccommodationRentFlow } from './flows/rent/propertyOwner/shortTermAccommodation/webView/webViewPropertyOwnerShortTermAccommodationRentFlow'
import { webViewTenantHouseRentFlow } from './flows/rent/tenant/house/webView/webViewTenantHouseRentFlow'
import { webViewPropertyOwnerApartmentSellFlow } from './flows/sell/propertyOwner/apartment/webView/webViewPropertyOwnerApartmentSellFlow'
import { webViewTenantShortTermAccommodationRentFlow } from './flows/rent/tenant/shortTermAccommodation/webView/webViewTenantShortTermAccommodationRentFlow'

export const FLOWS = [
  propertyOwnerApartmentRentFlow,
  propertyOwnerGarageRentFlow,
  propertyOwnerGastronomyRentFlow,
  propertyOwnerHangarRentFlow,
  propertyOwnerHouseRentFlow,
  propertyOwnerOfficeRentFlow,
  propertyOwnerStoreRentFlow,
  propertyOwnerShortTermAccommodationRentFlow,
  propertyOwnerIndustrialPlotRentFlow,
  propertyOwnerApartmentSellFlow,
  propertyOwnerGastronomySellFlow,
  propertyOwnerHangarSellFlow,
  propertyOwnerHouseSellFlow,
  propertyOwnerIndustrialPlotSellFlow,
  propertyOwnerOfficeSellFlow,
  propertyOwnerPlotSellFlow,
  propertyOwnerStoreSellFlow,
  propertyOwnerInvestmentSellFlow,
  propertyOwnerGarageSellFlow,
  tenantApartmentRentFlow,
  tenantGarageRentFlow,
  tenantGastronomyRentFlow,
  tenantHangarRentFlow,
  tenantHouseRentFlow,
  tenantOfficeRentFlow,
  tenantShortTermAccommodationRentFlow,
  tenantStoreRentFlow,
  tenantFlatShareRoomRentFlow,
  tenantIndustrialPlotRentFlow,
  agentApartmentRentFlow,
  agentFlatShareRoomRentFlow,
  agentShortTermAccommodationRentFlow,
  agentGarageRentFlow,
  agentApartmentSellFlow,
  agentHouseSellFlow,
  agentHouseRentFlow,
  agentOfficeRentFlow,
  agentGastronomyRentFlow,
  agentGastronomySellFlow,
  agentStoreRentFlow,
  agentOfficeSellFlow,
  agentProductionHallRentFlow,
  agentIndustrialPlotSellFlow,
  agentProductionHallSellFlow,
  agentPlotSellFlow,
  agentInvestmentSellFlow,
  agentStoreSellFlow,
  agentIndustrialPlotRentFlow,
  agentGarageSellFlow,
  agentWaitingListApartmentRentFlow,
]
const AppRouter: React.FC = () => {
  const { isDev } = useEnvironment()
  const { errorPage, replaceHistory } = useAppSelector((state: InitState) => state.errorHandlerFeature)
  const navigate = usePreserveSearchParamsNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isWebViewEnabled = isWebView()
  const flows = useMemo(() => {
    if (!isWebViewEnabled) {
      return FLOWS
    }

    // Replace desktop flows with web view versions where available
    return FLOWS.map((flow) => {
      if (flow.flowInformation.name === 'property_owner_apartment_rent') {
        return webViewPropertyOwnerApartmentRentFlow
      }
      if (flow.flowInformation.name === FlowName.PROPERTY_OWNER_HOUSE_RENT) {
        return webViewPropertyOwnerHouseRentFlow
      }
      if (flow.flowInformation.name === FlowName.PROPERTY_OWNER_HOUSE_SELL) {
        return webViewPropertyOwnerHouseSellFlow
      }
      if (flow.flowInformation.name === FlowName.TENANT_HOUSE_RENT) {
        return webViewTenantHouseRentFlow
      }
      if (flow.flowInformation.name === FlowName.PROPERTY_OWNER_APARTMENT_SELL) {
        return webViewPropertyOwnerApartmentSellFlow
      }
      if (flow.flowInformation.name === FlowName.PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT) {
        return webViewPropertyOwnerShortTermAccommodationRentFlow
      }
      if (flow.flowInformation.name === FlowName.TENANT_SHORT_TERM_ACCOMMODATION_RENT) {
        return webViewTenantShortTermAccommodationRentFlow
      }
      if (flow.flowInformation.name === FlowName.TENANT_APARTMENT_RENT) {
        return webViewTenantApartmentRentFlow
      }
      return flow
    })
  }, [isWebViewEnabled])

  useEffect(() => {
    if (errorPage) {
      navigate(errorPage, { replace: replaceHistory })
      // Resetting error page after navigation to prevent history issues
      // When transitioning from the insertion to the error page, the app is not reloaded thus the redux state stays as it is
      // If the user clicks on browser back, it navigates back but the page is still not reloaded and has the same redux state as before.
      // Now in case of another error, the hook would not be executed anymore because errorPage is already set and no change happened.
      dispatch(resetError())
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorPage, replaceHistory, dispatch])

  return (
    <Routes>
      <Route
        path={BASE_PATH}
        element={
          <Navigate replace to={preserveSearchParams(`${FLOWS[0].flowInformation.path}/:id`, location.search)} />
        }
      />
      {flows.map((flow) => (
        <Route key={flow.flowInformation.name} path={flow.flowInformation.path}>
          <Route
            index
            element={
              <ProtectedRoute>
                <InsertionEditorWrapper flow={flow} />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${flow.flowInformation.path}/:id`}
            element={
              <ProtectedRoute>
                <InsertionEditorWrapper flow={flow} />
              </ProtectedRoute>
            }
          />
        </Route>
      ))}
      <Route
        path={`${OLD_AGENT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH}/:id`}
        element={<NavigateWithId to={AGENT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH} />}
      />
      <Route
        path={`${OLD_AGENT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH}`}
        element={<Navigate to={AGENT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH} />}
      />
      <Route
        path={`${OLD_PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH}/:id`}
        element={<NavigateWithId to={PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH} />}
      />
      <Route
        path={`${OLD_PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH}`}
        element={<Navigate to={PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH} />}
      />
      <Route
        path={`${OLD_TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH}/:id`}
        element={<NavigateWithId to={TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH} />}
      />
      <Route
        path={`${OLD_TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH}`}
        element={<Navigate to={TENANT_SHORT_TERM_ACCOMMODATION_RENT_FLOW_PATH} />}
      />
      {isDev && <Route path={LOGIN_PATH} element={<LoginPage />} />}
      {isDev && <Route path={FLOW_CHOOSER_PATH} element={<FlowChooserPage />} />}
      <Route path={ERROR_PATH} element={<ErrorPage />} />
      <Route
        path={FORBIDDEN_PATH}
        element={<ErrorPage messageSubKey={'forbidden'} withLogoutLink withLinkToListingOverview />}
      />
      <Route
        path={LISTING_DELETED_PATH}
        element={<ErrorPage messageSubKey={'listingDeleted'} withLinkToListingOverview />}
      />
      <Route
        path={SESSION_NOT_FOUND_PATH}
        element={<ErrorPage messageSubKey={'sessionNotFound'} withLinkToListingOverview />}
      />
      <Route path={SEGMENTATION_PATH} element={<SegmentationPage />} />
      <Route path='/*' element={<NotFoundPage />} />
    </Routes>
  )
}

function NavigateWithId(props: NavigateProps) {
  const { id } = useParams<ListingParams>()
  const { to, ...restProps } = props
  return <Navigate replace to={`${to}/${id}`} {...restProps} />
}

export default AppRouter
